import { TeamDeal } from '../../models/dto/team-deal';
import {
  Action,
  Selector,
  State,
  StateContext,
  createSelector, Store
} from '@ngxs/store';
import { Injectable } from '@angular/core';
import { LenderUserDealsService, TeamDealsService } from '../../modules/team-deals/services/team-deals.service';
import { TeamDealsActions, TeamDealsActions as TDA } from './team-deals.action';
import { catchError } from 'rxjs/operators';
import { of, tap, throwError } from 'rxjs';
import { append, compose, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { DealUtility } from 'ng2-app/util/deal/deal.utility';
import { isToday } from 'date-fns';
import GetTeamDeals = TeamDealsActions.GetTeamDeals;
import { uniqBy } from 'lodash';

export interface ExcludedDeal extends Partial<TeamDeal> {
  excludedDate: number;
}

// Inherit the parent state model with correct entity type.
export class TeamDealsStateModel {
  deals: TeamDeal[];
  excludedDeals: ExcludedDeal[];
  excludedLenderIds: number[];
  loading: boolean;
  selectedTeamId: number;
  selectedUserIds: number[];
  dealsTypeFilter: string;
  error?: any;
}

@State<Partial<TeamDealsStateModel>>({
	name: 'TeamDeals',
	defaults: {
    deals: [],
    excludedLenderIds: [],
    excludedDeals: [],
    loading: false,
    dealsTypeFilter: 'all',
    selectedUserIds: [],
  }
})

@Injectable()
export class TeamDealsState {

  static localStorageKey: string = 'teamDealsSettings';

  @Selector()
  static selectedTeamId(state: TeamDealsStateModel) {
    return state.selectedTeamId;
  }

  @Selector()
  static deals(state: TeamDealsStateModel) {
    return state.deals;
  }

  @Selector()
  static allLenders(state: TeamDealsStateModel) {
    return uniqBy(
      state.deals.map(( { lenderName, loanProductLenderId } ) => ({ lenderName, loanProductLenderId })),
      'loanProductLenderId'
    );
  }

  @Selector()
  static excludedLenderIds(state: TeamDealsStateModel) {
    return state.excludedLenderIds;
  }

  @Selector()
	static contracts(includeClosingToday: boolean) {
    return createSelector([TeamDealsState], ({ TeamDeals }) => {
      const contractDeals = this.contractDeals(TeamDeals?.deals, includeClosingToday);
      return this.filterByDealType(
        contractDeals,
        TeamDeals.dealsTypeFilter,
        TeamDeals.excludedDeals,
        TeamDeals.excludedLenderIds,
      );
    });
  }

  @Selector()
  static funded(state: TeamDealsStateModel) {
    const fundedDeals = this.fundedDeals(state.deals);
    return this.filterByDealType(
      fundedDeals,
      state.dealsTypeFilter,
      state.excludedDeals,
      state.excludedLenderIds,
    );
  }

  @Selector()
	static closingToday(state: TeamDealsStateModel) {
    const closingDeals = this.closingTodayDeals(state.deals);
    return this.filterByDealType(
      closingDeals,
      state.dealsTypeFilter,
      state.excludedDeals,
      state.excludedLenderIds,
    );
  }

  @Selector()
  static offerDeals(state: TeamDealsStateModel) {
    const offeredDeals = this.offeredDeals(state.deals);
    const selectedUserIds = state?.selectedUserIds;
    const bySelectedUserIds = selectedUserIds?.length
           ? offeredDeals?.filter( d => selectedUserIds.includes(d.lenderUserId) )
           : offeredDeals;
    return this.filterByDealType(
      bySelectedUserIds,
      state.dealsTypeFilter,
      state.excludedDeals,
      state.excludedLenderIds,
    );
  }

  @Selector()
  static excludedDeals(state: TeamDealsStateModel) {
    const currentTeamId = state.selectedTeamId;
    return state.excludedDeals.filter( deal => deal.teamId === currentTeamId );
  }

  @Selector()
  static dealsTypeFilter(state: TeamDealsStateModel) {
    return state.dealsTypeFilter;
  }

  @Selector()
  static loading(state: TeamDealsStateModel) {
    return state.loading;
  }

  static offeredDeals(deals: TeamDeal[] = []) {
    return deals.filter( deal => {
      return DealUtility.offerDealStatuses.includes(deal.status);
    });
  }

  static contractDeals(deals: TeamDeal[] = [], includeClosingToday: boolean) {
    return deals.filter( deal => {
      return (['offerAccepted', 'contractOut', 'contractIn'].includes(deal.status) || deal.stage === 'closing')
        && (includeClosingToday || !deal.dealFlagId);
    });
  }

  static fundedDeals(deals: TeamDeal[] = []) {
    return deals.filter( deal => {
      return deal.stage === 'funded'
        && !!deal.timeInStage
        && isToday(new Date(deal.timeInStage));
    });
  }

  static closingTodayDeals(deals: TeamDeal[] = []) {
    return deals.filter( deal => {
      return deal.stage !== 'funded' && !!deal.dealFlagId;
    });
  }

  static filterByDealType( deals, dealType, excludedDeals, excludedLenderIds ) {
    return deals.filter( deal => {
      const excluded = !!excludedDeals.find( exDeal => deal.id === exDeal.id);
      const lenderExcluded = excludedLenderIds.includes(deal.loanProductLenderId);
      const correctType = dealType === 'onlyEmbedded'
        ? !!deal.embeddedUserId
        : dealType === 'nonEmbedded'
          ? !deal.embeddedUserId
          : true;
      return !excluded && !lenderExcluded && correctType;
    });
  }

  // DI creates a concrete service that implements AbstractCrudService for
  // use by the parent EntityState.
	constructor(
    private teamDealsService: TeamDealsService,
    private lenderUserDealsService: LenderUserDealsService,
    private store: Store,
  ) {}

  // If you only need generic CRUD action and selectors, you're done :).
  // Or you can implement additional stuff specific to this store using its
  // actions.ts file.
  @Action(TDA.GetTeamDeals)
  GetTeamDeals(
    ctx: StateContext<TeamDealsStateModel>,
    { id }: TDA.GetTeamDeals
  ) {
    ctx.patchState({
      loading: true,
    });
    return this.teamDealsService.get(id).pipe(
      catchError(err => {
        ctx.patchState({loading: false});
        return throwError(err);
      }),
      tap(response => {
        const deals = this.getDealsArrayFromResponse(response);
        ctx.patchState({
          deals,
          selectedTeamId: id,
          loading: false,
        });
        this.updateLocalStorage(ctx.getState());
      }),
    );
  }

  @Action(TDA.ExcludeDealFromTeamDeals)
  ExcludeDealFromTeamDeals(
    ctx: StateContext<TeamDealsStateModel>,
    { deal }: TDA.ExcludeDealFromTeamDeals) {
    const excludedDeal = {
      ...deal,
      excludedDate: Date.now()
    };
    ctx.setState(patch({
      excludedDeals: append<ExcludedDeal>([excludedDeal]),
    }));
    this.updateLocalStorage(ctx.getState());
  }

  @Action(TDA.UnExcludeDealFromTeamDeals)
  UnExcludeDealFromTeamDeals(
    ctx: StateContext<TeamDealsStateModel>,
    { dealId }: TDA.UnExcludeDealFromTeamDeals) {
    ctx.setState(patch({
      excludedDeals: removeItem<ExcludedDeal>( ed => ed.id === dealId),
    }));
    this.updateLocalStorage(ctx.getState());
  }

  @Action(TDA.ClearExcludedTeamDeals)
  ClearExcludedTeamDeals(
    ctx: StateContext<TeamDealsStateModel>,
    { }: TDA.ClearExcludedTeamDeals) {
    ctx.setState(patch({
      excludedDeals: [],
    }));
    this.updateLocalStorage(ctx.getState());
  }

  @Action(TDA.ExcludeLenderFromTeamDeals)
  ExcludeLenderFromTeamDeals(
    ctx: StateContext<TeamDealsStateModel>,
    { lenderId }: TDA.ExcludeLenderFromTeamDeals) {
    ctx.setState(patch({
      excludedLenderIds: append([lenderId]),
    }));
  }

  @Action(TDA.UnExcludeLenderFromTeamDeals)
  UnExcludeLenderFromTeamDeals(
    ctx: StateContext<TeamDealsStateModel>,
    { lenderId }: TDA.UnExcludeLenderFromTeamDeals) {
    ctx.setState(patch({
      excludedLenderIds: removeItem( elID => elID === lenderId),
    }));
  }

  @Action(TDA.ClearExcludedTeamDealsLenders)
  ClearExcludedTeamDealsLenders(
    ctx: StateContext<TeamDealsStateModel>,
    { }: TDA.ClearExcludedTeamDealsLenders) {
    ctx.setState(patch({
      excludedLenderIds: [],
    }));
  }

  @Action(TDA.SetTeamDealsExcludedLenders)
  SetTeamDealsExcludedLenders(
    ctx: StateContext<TeamDealsStateModel>,
    { lenderIds }: TDA.SetTeamDealsExcludedLenders) {
    ctx.setState(patch({
      excludedLenderIds: lenderIds,
    }));
  }

  @Action(TDA.GetTeamDealsLocalStorage)
  GetTeamDealsLocalStorage(
    ctx: StateContext<TeamDealsStateModel>,
    { }: TDA.GetTeamDealsLocalStorage) {
    const current = ctx.getState();
    const storageVal = this.getLocalStorageVal();
    const excludedDeals = (storageVal.excludedDeals || current.excludedDeals).filter( exDeal => {
      return isToday(new Date(exDeal.excludedDate));
    });
    const patchVal = {
      excludedDeals: excludedDeals,
      selectedTeamId: storageVal.selectedTeamId || current.selectedTeamId,
      dealsTypeFilter: storageVal.dealsTypeFilter || 'all',
    };
    ctx.setState(patch(patchVal));
    if(!current.selectedTeamId && !!storageVal.selectedTeamId) {
      this.store.dispatch( new GetTeamDeals(storageVal.selectedTeamId) );
    }
  }

  @Action(TDA.GetLenderUserDeals)
  GetLenderUserDeals(
    ctx: StateContext<TeamDealsStateModel>,
    payload: TDA.GetLenderUserDeals,
  ) {
    ctx.patchState({loading: true});
    return this.lenderUserDealsService.get(payload.id)
      .pipe(
        catchError( error => {
          ctx.patchState({
            loading: false,
            error,
          });
          return of(error);
        })
      )
      .subscribe( response => {
        const deals = this.getDealsArrayFromResponse(response);
        ctx.patchState({
          deals,
          loading: false,
        });
    });
  }

  @Action(TDA.TeamDealFlagAdded)
  teamDealFlagAdded(ctx: StateContext<TeamDealsStateModel>, payload: TDA.TeamDealFlagAdded) {
    const dealFlag = payload.dealFlag;
    ctx.setState(patch({
      deals: updateItem( (deal: TeamDeal) =>  deal.id === dealFlag.dealId, patch({
        dealFlagId: dealFlag.id,
        dealFlag: dealFlag.flag
      })),
    }));
  }

  @Action(TDA.TeamDealFlagRemoved)
  teamDealFlagRemoved(ctx: StateContext<TeamDealsStateModel>, { dealId }) {
    ctx.setState(patch({
      deals: updateItem( (deal: TeamDeal) =>  deal.id === dealId, patch({
        dealFlagId: undefined,
        dealFlag: undefined,
      })),
    }));
  }

  @Action(TDA.SetSelectedUserIds)
  setSelectedUserIds(ctx: StateContext<TeamDealsStateModel>, { userIds }) {
    ctx.patchState({
      selectedUserIds: userIds,
    });
  }

  @Action(TDA.UpdateDealNoteContent)
  updateDealNoteContent(ctx: StateContext<TeamDealsStateModel>, { dealNote }) {
    const { dealId, content } = dealNote;
    ctx.setState(patch({
      deals: this.patchDealNote(dealId, content),
    }));
  }

  @Action(TDA.UpdateBorrowerNoteContent)
  updateBorrowerNoteContent(
    ctx: StateContext<TeamDealsStateModel>,
    {note}
  ) {
    const { borrowerId, content } = note;
    if(!!borrowerId && !!content) {
      const deals = ctx.getState().deals;
      ctx.setState(patch({
        deals: this.patchBorrowerNote(deals, note.borrowerId, note.content),
      }));
    }
  }

  @Action(TDA.SetDealsTypeFilter)
  setDealsTypeFilter(
    ctx: StateContext<TeamDealsStateModel>,
    { dealsTypeFilter }
  ) {
    ctx.patchState({
      dealsTypeFilter
    });
    this.updateLocalStorage(ctx.getState());
  }

  private getDealsArrayFromResponse(response) {
    const oldStructure = !!response.offers && !!response.contracts && !!response.funded;
    return !oldStructure ? response : [
      ...response.offers || [],
      ...response.contracts || [],
      ...response.funded || [],
    ];
  }

  private patchBorrowerNote(deals, borrowerId, borrowerNoteContent) {
    const indexes = deals.reduce( (res, cur, index) => {
      return cur.borrowerId === borrowerId
             ? [...res, index]
             : res;
    }, []);
    return compose<TeamDeal[]>(...indexes.map( (i) => updateItem<TeamDeal>(i, patch({
      latestBorrowerNote: borrowerNoteContent,
    }))));
  }

  private patchDealNote(dealId, dealNoteContent) {
    return updateItem( (d: TeamDeal) => d.id === dealId, patch({
      latestDealNote: dealNoteContent,
    }));
  }

  private getLocalStorageVal() {
    return JSON.parse(localStorage.getItem(TeamDealsState.localStorageKey) || '{}');
  }

  private updateLocalStorage(state) {
    const storageVal = JSON.stringify({
      selectedTeamId: state.selectedTeamId,
      excludedDeals: state.excludedDeals,
      dealsTypeFilter: state.dealsTypeFilter || 'all',
    });
    localStorage.setItem( TeamDealsState.localStorageKey, storageVal );
  }

}
